import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bug, CheckCircle2, RefreshCw, Trash2, Filter, AlertTriangle, Globe, User } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const SEVERITY_COLOR = {
  info:     "text-sky-300 border-sky-500/40 bg-sky-500/10",
  warning:  "text-amber-300 border-amber-500/40 bg-amber-500/10",
  error:    "text-rose-300 border-rose-500/40 bg-rose-500/10",
  critical: "text-fuchsia-300 border-fuchsia-500/50 bg-fuchsia-500/10",
};

const FILTERS = [
  { key: "unresolved", label: "Unresolved", q: { resolved: false } },
  { key: "critical",   label: "Critical",   q: { severity: "critical" } },
  { key: "error",      label: "Errors",     q: { severity: "error" } },
  { key: "warning",    label: "Warnings",   q: { severity: "warning" } },
  { key: "all",        label: "All",        q: {} },
];

function ErrorRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const meta = SEVERITY_COLOR[row.severity] || SEVERITY_COLOR.error;

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/client-errors/${row.id}/resolve`);
      toast.success("Resolved");
      onChange && onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Resolve failed"));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this error log?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/client-errors/${row.id}`);
      toast.success("Deleted");
      onChange && onChange();
    } catch (err) {
      toast.error(errorMessage(err, "Delete failed"));
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      data-testid={`client-error-${row.id}`}
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-md border ${meta} p-3`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 ${meta.split(" ")[0]} mt-0.5 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm text-white font-medium truncate">{row.message}</div>
            <span className={`text-[10px] uppercase tracking-wider ${meta.split(" ")[0]}`}>
              {row.severity}{row.resolved ? " · resolved" : ""}
            </span>
          </div>
          <div className="text-xs text-white/55 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {row.component && <span>component: <span className="text-gold/80">{row.component}</span></span>}
            {row.url && <span className="inline-flex items-center gap-1"><Globe size={10}/> {row.url.replace(/^https?:\/\/[^/]+/, "")}</span>}
            {row.user_email && <span className="inline-flex items-center gap-1"><User size={10}/> {row.user_email}</span>}
            <span>{new Date(row.created_at).toLocaleString()}</span>
          </div>
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-[11px] text-gold/70 hover:text-gold mt-2"
          >
            {expanded ? "Hide details" : "Show stack & context"}
          </button>
          {expanded && (
            <div className="mt-2 space-y-2">
              {row.stack && (
                <pre className="text-[11px] text-white/60 bg-black/40 border border-white/10 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap">
                  {row.stack}
                </pre>
              )}
              {row.extra && Object.keys(row.extra).length > 0 && (
                <pre className="text-[11px] text-white/60 bg-black/40 border border-white/10 rounded p-2 overflow-auto max-h-32">
                  {JSON.stringify(row.extra, null, 2)}
                </pre>
              )}
              <div className="text-[10px] text-white/40">
                IP: {row.client_ip || "—"} · UA: <span className="break-all">{row.user_agent || "—"}</span>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            {!row.resolved && (
              <button
                data-testid={`client-error-resolve-${row.id}`}
                onClick={resolve} disabled={busy}
                className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/40 rounded-md px-3 py-1.5 disabled:opacity-50 transition-colors"
              ><CheckCircle2 className="w-3 h-3" /> Mark resolved</button>
            )}
            <button
              data-testid={`client-error-delete-${row.id}`}
              onClick={remove} disabled={busy}
              className="inline-flex items-center gap-1 text-xs text-rose-300 hover:bg-rose-500/10 border border-rose-500/40 rounded-md px-3 py-1.5 disabled:opacity-50 transition-colors"
            ><Trash2 className="w-3 h-3" /> Delete</button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminClientErrorsTab() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unresolved: 0 });
  const [filter, setFilter] = useState("unresolved");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const f = FILTERS.find((x) => x.key === filter)?.q || {};
      const r = await api.get("/admin/client-errors", { params: { ...f, limit: 100 } });
      setItems(r.data?.items || []);
      setCounts({ total: r.data?.total || 0, unresolved: r.data?.unresolved || 0 });
    } catch (err) {
      toast.error(errorMessage(err, "Load failed"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="admin-client-errors-tab" className="space-y-4">
      <div className="rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 p-4 flex items-start gap-3">
        <Bug className="w-5 h-5 text-fuchsia-300 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-fuchsia-200 font-display text-base">Client Error Inbox</div>
          <div className="text-xs text-white/65 mt-1">
            Render crashes, server failures, and network errors hit by real users — auto-piped here so you spot regressions before they're reported. {counts.unresolved} unresolved · {counts.total} total.
          </div>
        </div>
        <button onClick={load} className="text-white/50 hover:text-fuchsia-300 p-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-gold/70" />
        {FILTERS.map((f) => (
          <button
            key={f.key} data-testid={`error-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              filter === f.key
                ? "bg-gold/15 text-gold border-gold"
                : "border-white/10 text-white/60 hover:border-gold/40 hover:text-gold"
            }`}
          >{f.label}</button>
        ))}
      </div>

      {loading && <div className="text-center text-white/50 py-10">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center text-white/50 py-12 border border-dashed border-white/10 rounded-md">
          Inbox empty — no errors in this view. 🌟
        </div>
      )}
      <div className="space-y-3">
        {items.map((r) => <ErrorRow key={r.id} row={r} onChange={load} />)}
      </div>
    </div>
  );
}
